import { MapStyle } from 'appConstants';
import { IMetricLegendConfig } from 'features/workflow_timeline/metricsOptions';

import { getMetricColorSchemes } from '../chart/colorMaps';

export const TL_METRIC_TYP_REL_VOL = 'count_typ'; // c
export const TL_METRIC_TYP_REL_SPEED_VOL = 'count_speed_typ'; // a
export const TL_METRIC_FF_REL_SPEED_VOL = 'count_speed_ff'; // b
export const TL_METRIC_DTR_VOL = 'count_dtr'; // d

const METRIC_PLACEHOLDER = 'metric';

const speeds = (timeOfDayOffset) => ['get', `s${timeOfDayOffset}`];
const typicalSpeeds = (timeOfDayOffset) => ['get', `ts${timeOfDayOffset}`];
const counts = (timeOfDayOffset) => ['get', `c${timeOfDayOffset}`];
const typicalCounts = (timeOfDayOffset) => ['get', `tc${timeOfDayOffset}`];

export function metricExprDeepSub(expr: Array<any>, sub) {
  // Substitutes METRIC_PLACEHOLDER for the passed sub
  for (let i = 0; i < expr.length; i++) {
    if (expr[i] === METRIC_PLACEHOLDER) {
      expr[i] = sub;
    } else if (Array.isArray(expr[i])) {
      expr[i] = metricExprDeepSub(expr[i], sub);
    }
  }
  return expr;
}

const ff_relative_speed_expr = ({ timeOfDayOffset }) => [
  '/',
  ['coalesce', speeds(timeOfDayOffset), typicalSpeeds(timeOfDayOffset)],
  ['get', 'freeflow_speed_kph'],
];
const typ_relative_speed_expr = ({ timeOfDayOffset }) => [
  'coalesce',
  [
    '/',
    ['coalesce', speeds(timeOfDayOffset), typicalSpeeds(timeOfDayOffset)],
    typicalSpeeds(timeOfDayOffset),
  ],
  1,
];

const typ_relative_count_expr = ({ timeOfDayOffset }) => [
  'interpolate',
  ['linear'],
  [
    '*',
    [
      '-',
      [
        '/',
        ['coalesce', counts(timeOfDayOffset), typicalCounts(timeOfDayOffset)],
        ['+', typicalCounts(timeOfDayOffset), 1],
      ],
      1,
    ],
    [
      'interpolate',
      ['linear'],
      ['max', typicalCounts(timeOfDayOffset), counts(timeOfDayOffset)],
      0,
      -0.1,
      50,
      -1,
    ],
  ],
  -5,
  -2,
  -1,
  -1,
  1,
  1,
];

const max_relative_count_expr = ({ timeOfDayOffset, hourlyMaxCount }) => [
  'let',
  'counts_',
  ['coalesce', counts(timeOfDayOffset), typicalCounts(timeOfDayOffset)],
  [
    'let',
    'typ_',
    ['+', typicalCounts(timeOfDayOffset), 1],
    [
      'interpolate',
      ['linear'],
      [
        '*',
        [
          '-',
          [
            'case',
            ['>', ['var', 'counts_'], ['var', 'typ_']],
            [
              '+',
              1,
              [
                '/',
                ['-', ['var', 'counts_'], ['var', 'typ_']],
                ['literal', hourlyMaxCount],
              ],
            ],
            ['/', ['var', 'counts_'], ['var', 'typ_']],
          ],
          1,
        ],
        [
          'interpolate',
          ['linear'],
          ['max', typicalCounts(timeOfDayOffset), counts(timeOfDayOffset)],
          0,
          -0.1,
          50,
          -1,
          75,
          -1.5,
          100,
          -2,
        ],
      ],
      -5,
      -2,
      -1,
      -2,
      0,
      0,
      1,
      1,
    ],
  ],
];

export const counts_absolute_size_expr = (
  timeOfDayOffset,
  hourlyMaxCount,
  base,
) => [
  'interpolate',
  ['linear'],
  [
    '/',
    [
      '*',
      ['max', counts(timeOfDayOffset), typicalCounts(timeOfDayOffset)],
      100,
    ],
    1000,
  ],
  0,
  0.5 * base,
  10,
  1.5 * base,
  100,
  3.0 * base,
];

const legend_stops_0_1_perc = [
  {
    value: 0,
    label: ['Congested', '0'],
  },
  {
    value: 0.2,
    label: ['', '20'],
  },
  {
    value: 0.4,
    label: ['', '40'],
  },
  {
    value: 0.6,
    label: ['', '60'],
  },
  {
    value: 0.8,
    label: ['', '80'],
  },
  {
    value: 1,
    label: ['Freeflow', '100'],
  },
];

const legend_stops_atypical_volume = [
  {
    value: -2,
    label: ['Higher than normal', '500'],
  },
  {
    value: -1,
    label: ['', '100'],
  },
  {
    value: -0.5,
    label: ['', '50'],
  },
  {
    value: -0.25,
    label: ['', ''],
  },
  {
    value: 0,
    label: ['', 'Typical'],
  },
  {
    value: 0.25,
    label: ['', ''],
  },
  {
    value: 1,
    label: ['Lower than normal', '-100'],
  },
];

const legend_stops_detour_volume = [
  {
    value: -2,
    label: ['All detoured', '100'],
  },
  {
    value: -1.5,
    label: ['', '75'],
  },
  {
    value: -1,
    label: ['', '50'],
  },
  {
    value: -0.25,
    label: ['', ''],
  },
  {
    value: 0,
    label: ['', 'Typical'],
  },
  {
    value: 0.25,
    label: ['', ''],
  },
  {
    value: 1,
    label: ['Lower than normal', '-100'],
  },
];

export function getDetourMetricOptions(
  mapStyle: MapStyle,
  metric: string,
): IMetricLegendConfig {
  switch (metric) {
    case TL_METRIC_FF_REL_SPEED_VOL:
      return {
        metric_expr: ff_relative_speed_expr,
        color_schemes: getMetricColorSchemes('0_1_stops', mapStyle),
        legend_stops: legend_stops_0_1_perc,
        display_volume_thickness: false,
        is_metric_diverging: false,
      };
    case TL_METRIC_TYP_REL_SPEED_VOL:
      return {
        metric_expr: typ_relative_speed_expr,
        color_schemes: getMetricColorSchemes('0_1_stops', mapStyle),
        legend_stops: legend_stops_0_1_perc,
        display_volume_thickness: false,
        is_metric_diverging: false,
      };
    case TL_METRIC_TYP_REL_VOL:
      return {
        metric_expr: typ_relative_count_expr,
        color_schemes: getMetricColorSchemes(
          'detour_atypical_volume_stops',
          mapStyle,
        ),
        legend_stops: legend_stops_atypical_volume,
        width: 'count_absolute',
        is_metric_diverging: true,
      };
    case TL_METRIC_DTR_VOL:
      return {
        metric_expr: max_relative_count_expr,
        color_schemes: getMetricColorSchemes('detour_volume_stops', mapStyle),
        legend_stops: legend_stops_detour_volume,
        width: 'count_absolute',
        is_metric_diverging: true,
      };
  }
}
